import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import axios from 'axios';
import { reportsAPI } from '../utils/api';

const API = process.env.REACT_APP_API_URL || 'https://clicksaver.app';

async function apiGet(path) {
  const token = await auth.currentUser?.getIdToken();
  return axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}
async function apiPatch(path, data) {
  const token = await auth.currentUser?.getIdToken();
  return axios.patch(`${API}${path}`, data, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}
async function apiPost(path, data) {
  const token = await auth.currentUser?.getIdToken();
  return axios.post(`${API}${path}`, data, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}

/* Toggle Switch */
function Toggle({ value, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 48, height: 26, borderRadius: 13, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: value ? 'var(--accent-green)' : 'var(--border)',
        transition: 'background 0.2s', position: 'relative', flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: value ? 24 : 3,
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {value ? (
          <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 12 12"><path d="M3 3l6 6M9 3l-6 6" stroke="#999" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>
        )}
      </div>
    </button>
  );
}

export default function Notifications() {
  const [notifEmails, setNotifEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [notifyGoogleAds, setNotifyGoogleAds] = useState(false);
  const [notifyWeekly, setNotifyWeekly] = useState(true);
  const [notifyMonthly, setNotifyMonthly] = useState(true);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState(25);
  const [alertMinClicks, setAlertMinClicks] = useState(20);
  const [alertWebhook, setAlertWebhook] = useState('');
  const [alertTestState, setAlertTestState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testState, setTestState] = useState({});
  const [emailTestStatus, setEmailTestStatus] = useState({});

  useEffect(() => {
    apiGet('/api/auth/notifications').then(n => {
      let emails = n.notification_emails || [];
      // Auto-add the registered account email if no emails saved yet
      if (emails.length === 0 && auth.currentUser?.email) {
        emails = [auth.currentUser.email];
        apiPatch('/api/auth/notifications', { notification_emails: emails }).catch(() => {});
      }
      setNotifEmails(emails);
      setNotifyGoogleAds(n.notify_google_ads ?? false);
      setNotifyWeekly(n.notify_weekly_report ?? true);
      setNotifyMonthly(n.notify_monthly_report ?? true);
      setAlertEnabled(n.alert_enabled ?? false);
      setAlertThreshold(n.alert_fraud_threshold ?? 25);
      setAlertMinClicks(n.alert_min_clicks ?? 20);
      setAlertWebhook(n.alert_webhook_url ?? '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveNotification = async (updates) => {
    setSaving(true);
    try { await apiPatch('/api/auth/notifications', updates); } catch {}
    setSaving(false);
  };

  const sendEmailTest = async (email) => {
    setEmailTestStatus(s => ({ ...s, [email]: { state: 'sending' } }));
    try {
      await reportsAPI.testEmail(email);
      setEmailTestStatus(s => ({ ...s, [email]: { state: 'sent' } }));
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Send failed';
      setEmailTestStatus(s => ({ ...s, [email]: { state: 'failed', msg } }));
    }
  };

  const handleAddEmail = async () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@')) return;
    if (notifEmails.length >= 5) return alert('Maximum 5 email addresses allowed.');
    if (notifEmails.includes(email)) return;
    const updated = [...notifEmails, email];
    setNotifEmails(updated);
    setNewEmail('');
    await saveNotification({ notification_emails: updated });
    sendEmailTest(email);
  };

  const handleRemoveEmail = async (email) => {
    const updated = notifEmails.filter(e => e !== email);
    setNotifEmails(updated);
    setEmailTestStatus(s => { const n = { ...s }; delete n[email]; return n; });
    await saveNotification({ notification_emails: updated });
  };

  const handleSendTest = async (type) => {
    setTestState(s => ({ ...s, [type]: 'sending' }));
    try {
      await reportsAPI.sendTest(type);
      setTestState(s => ({ ...s, [type]: 'sent' }));
      setTimeout(() => setTestState(s => ({ ...s, [type]: null })), 3000);
    } catch {
      setTestState(s => ({ ...s, [type]: 'error' }));
      setTimeout(() => setTestState(s => ({ ...s, [type]: null })), 3000);
    }
  };

  const TestButton = ({ type }) => {
    const state = testState[type];
    return (
      <button
        onClick={() => handleSendTest(type)}
        disabled={state === 'sending'}
        style={{
          padding: '6px 14px', fontSize: 11, flexShrink: 0, cursor: state === 'sending' ? 'wait' : 'pointer',
          border: `1px solid ${state === 'sent' ? 'var(--accent-green)' : state === 'error' ? 'var(--accent-red)' : 'var(--border)'}`,
          color: state === 'sent' ? 'var(--accent-green)' : state === 'error' ? 'var(--accent-red)' : 'var(--text-muted)',
          borderRadius: 6, fontWeight: 500, background: 'transparent', transition: 'all 0.2s',
        }}
      >
        {state === 'sending' ? 'Sending...' : state === 'sent' ? 'Sent!' : state === 'error' ? 'Failed' : 'Send Test'}
      </button>
    );
  };

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Manage Notifications</h1>
        <p>Control email alerts and reporting preferences</p>
      </div>

      {/* Notification Emails */}
      <div className="card" style={{ maxWidth: 750, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Notification Email</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Enter an email address to receive notifications (Up to 5 addresses).
            </div>
          </div>
        </div>

        {/* Email list */}
        {notifEmails.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {notifEmails.map(email => {
              const ts = emailTestStatus[email] || {};
              const { state, msg } = ts;
              return (
                <div key={email} style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${state === 'sent' ? 'rgba(34,197,94,0.3)' : state === 'failed' ? 'rgba(248,81,73,0.3)' : 'var(--border)'}`, transition: 'border-color 0.3s' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--bg-secondary)', padding: '10px 14px', gap: 12,
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{email}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {state === 'sending' && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sending...</span>}
                      {state === 'sent' && <span style={{ fontSize: 11, color: 'var(--accent-green)', fontWeight: 600 }}>✓ Test sent</span>}
                      {state === 'failed' && <span style={{ fontSize: 11, color: 'var(--accent-red)', fontWeight: 600 }}>✗ Failed</span>}
                      <button
                        onClick={() => sendEmailTest(email)}
                        disabled={state === 'sending'}
                        style={{
                          fontSize: 11, padding: '4px 10px', cursor: state === 'sending' ? 'wait' : 'pointer',
                          border: '1px solid var(--border)', borderRadius: 5,
                          background: 'transparent', color: 'var(--text-muted)', fontWeight: 500,
                        }}
                      >
                        {state === 'sending' ? '...' : state === 'sent' ? 'Resend' : 'Send Test'}
                      </button>
                      <span onClick={() => handleRemoveEmail(email)}
                        style={{ color: 'var(--accent-red)', cursor: 'pointer', fontWeight: 700, fontSize: 18, lineHeight: 1, opacity: 0.7 }}>&times;</span>
                    </div>
                  </div>
                  {state === 'failed' && msg && (
                    <div style={{ fontSize: 11, color: 'var(--accent-red)', padding: '6px 14px', background: 'rgba(248,81,73,0.06)', borderTop: '1px solid rgba(248,81,73,0.15)' }}>
                      {msg}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="email"
            placeholder="Enter email address..."
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddEmail()}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button
            onClick={handleAddEmail}
            disabled={notifEmails.length >= 5}
            style={{
              padding: '8px 20px', fontSize: 13, flexShrink: 0, cursor: 'pointer',
              border: '2px solid var(--accent-orange)', color: 'var(--accent-orange)',
              borderRadius: 20, fontWeight: 600, background: 'transparent',
            }}
          >
            Add New
          </button>
        </div>
      </div>

      {/* Google Ads Notifications */}
      <div className="card" style={{ maxWidth: 750, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Google Ads</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              By enabling, we will notify you by email every time an IP has been detected and blocked.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <TestButton type="block-alert" />
            <Toggle value={notifyGoogleAds} onChange={v => { setNotifyGoogleAds(v); saveNotification({ notify_google_ads: v }); }} disabled={saving} />
          </div>
        </div>
      </div>

      {/* Weekly Report */}
      <div className="card" style={{ maxWidth: 750, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Weekly Report</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              By enabling this feature, You will get weekly reports regarding the Domain traffic and Security performance.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <TestButton type="weekly" />
            <Toggle value={notifyWeekly} onChange={v => { setNotifyWeekly(v); saveNotification({ notify_weekly_report: v }); }} disabled={saving} />
          </div>
        </div>
      </div>

      {/* Monthly Analytics Report */}
      <div className="card" style={{ maxWidth: 750, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Monthly Analytics Report</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              By enabling this feature, you will receive a monthly report detailing all click fraud insights on your domain including personalized recommendations.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <TestButton type="monthly" />
            <Toggle value={notifyMonthly} onChange={v => { setNotifyMonthly(v); saveNotification({ notify_monthly_report: v }); }} disabled={saving} />
          </div>
        </div>
      </div>

      {/* Real-time Fraud Spike Alerts */}
      <div className="card" style={{ maxWidth: 750, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              Fraud spike alerts
              <span style={{ fontSize: 10, fontWeight: 600, background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: 999 }}>LIVE</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Get an email and optional Slack / Discord ping whenever a campaign's fraud rate crosses your threshold. Checked every 15 minutes.
            </div>
          </div>
          <Toggle value={alertEnabled} onChange={v => { setAlertEnabled(v); saveNotification({ alert_enabled: v }); }} disabled={saving} />
        </div>

        {alertEnabled && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label>Fraud rate threshold</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min="1" max="100" value={alertThreshold}
                    onChange={e => setAlertThreshold(e.target.value)}
                    onBlur={() => saveNotification({ alert_fraud_threshold: Number(alertThreshold) })} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>%</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Alert when hourly rate exceeds this.</div>
              </div>
              <div>
                <label>Minimum sample size</label>
                <input type="number" min="1" value={alertMinClicks}
                  onChange={e => setAlertMinClicks(e.target.value)}
                  onBlur={() => saveNotification({ alert_min_clicks: Number(alertMinClicks) })} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Ignore samples smaller than this.</div>
              </div>
            </div>
            <div>
              <label>Slack / Discord / generic webhook URL (optional)</label>
              <input type="url" placeholder="https://hooks.slack.com/services/..." value={alertWebhook}
                onChange={e => setAlertWebhook(e.target.value)}
                onBlur={() => saveNotification({ alert_webhook_url: alertWebhook.trim() })} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Leave blank for email-only alerts.</div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className="btn btn-ghost" disabled={alertTestState === 'sending'}
                onClick={async () => {
                  setAlertTestState('sending');
                  try {
                    const r = await apiPost('/api/auth/notifications/test-alert', {});
                    const parts = [];
                    if (r.email === true) parts.push('email sent');
                    if (r.webhook === true) parts.push('webhook sent');
                    if (r.email === false) parts.push('email failed');
                    if (r.webhook === false) parts.push('webhook failed');
                    setAlertTestState(parts.length ? parts.join(' · ') : 'nothing to send');
                  } catch { setAlertTestState('request failed'); }
                }}
              >Send test alert</button>
              {alertTestState && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{alertTestState}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
